/**
 * The game screen: the engine's entry point for the shell (see contract.ts). Owns one
 * GameController per mount — the shell remounts the screen for a new race or role — and
 * renders the canvas plus the HUD on top of it.
 */
import { avatarEmoji, type PlayerId } from '@cube-rush/shared';
import { useLayoutEffect, useMemo, useRef } from 'preact/hooks';
import type { GameScreenProps } from './contract';
import type { EngineDevHooks } from './debug';
import { GameController } from './GameController';
import type { HudActions } from './hud/actions';
import { Hud } from './hud/Hud';
import './GameScreen.css';

export interface GameScreenDevProps {
  /** Dev harness only: clock override, per-frame hook, debug API. */
  dev?: EngineDevHooks;
}

export function GameScreen(props: GameScreenProps & GameScreenDevProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // One controller per mount: race and role are fixed for the screen's lifetime.
  const controller = useMemo(() => new GameController(props, { dev: props.dev }), []);
  controller.setProps(props);

  // A layout effect: its cleanup runs right at unmount. A passive effect's cleanup would wait
  // for the next paint (Preact, like React) — the game would run one more frame after the
  // shell switched screens: sounds, network messages, a second loop next to a new screen.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (root && canvas) controller.mount(root, canvas);
    return () => controller.dispose();
  }, [controller]);

  const actions = useMemo<HudActions>(
    () => ({
      useBoost: (slot) => controller.useBoost(slot),
      setMenu: (open) => controller.setMenu(open),
      openSettings: () => controller.openSettings(),
      requestQuit: () => controller.requestQuit(),
      switchRacer: (step) => controller.switchRacer(step),
      toggleMute: () => props.voice.toggleMute(),
      retryMic: () => props.voice.retryMic(),
    }),
    [controller, props.voice],
  );

  const nameOf = (id: PlayerId) => {
    const member = props.members.peek().find((m) => m.id === id);
    const profile = member?.profile ?? (id === props.me.id ? props.me.profile : null);
    return { name: profile?.name ?? '?', avatar: profile ? avatarEmoji(profile.avatar) : '❔' };
  };

  return (
    <div class="game" ref={rootRef}>
      <canvas class="game__canvas" ref={canvasRef} />
      <Hud
        hud={controller.hud}
        dom={controller.dom}
        actions={actions}
        voice={props.voice}
        nameOf={nameOf}
      />
    </div>
  );
}
